import { Alert, Button, LoadingState, Modal, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'

/**
 * Confirmação da conclusão EM CADEIA (SPEC-20261006-1027-concluir-em-cadeia).
 *
 * Concluir um item arrasta o que está embaixo: as atividades dos checklists, os
 * itens vinculados a elas e os filhos, em cadeia. Isso é muita coisa para
 * acontecer atrás de um clique numa caixinha — a lista aparece antes, e a
 * pessoa confirma ou fecha sem concluir nada.
 *
 * A lista vem do servidor, e não montada aqui: a tela conhece o checklist que
 * está à vista, não a árvore inteira abaixo dele.
 */
export function ConcluirEmCadeiaModal({
  itemId,
  titulo,
  onFechar,
  onConcluir,
  concluindo = false,
}: {
  itemId: string
  titulo: string
  onFechar: () => void
  /**
   * Quem chama faz a conclusão (a trilha da lista de Itens conclui pelo
   * pedido dela, com o status escolhido — SPEC-20261007-2155-trilha-de-etapas-e-status). Sem isto, a
   * janela conclui pela rota de sempre.
   */
  onConcluir?: () => void
  concluindo?: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const previa = useQuery({
    queryKey: ['work-item', itemId, 'cascata'],
    queryFn: async () =>
      (await api['work-items'][':id'].cascata.$get({ param: { id: itemId } })).json(),
    // a árvore muda com o que a pessoa acabou de fazer; não vale cache velho
    staleTime: 0,
  })

  const concluir = useMutation({
    mutationFn: async () =>
      (await api['work-items'][':id'].cascata.$post({ param: { id: itemId } })).json(),
    onSuccess: (dados) => {
      void queryClient.invalidateQueries({ queryKey: ['work-item'] })
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
      if (dados.recusados.length > 0) {
        toast.push({
          tone: 'danger',
          message: `${dados.recusados.length} item(ns) não puderam ser concluídos: ${dados.recusados
            .map((r) => r.codigo ?? r.id)
            .join(', ')}.`,
        })
      } else {
        toast.push({ tone: 'success', message: 'Concluído, com tudo o que estava embaixo.' })
      }
      onFechar()
    },
    onError: (e) =>
      toast.push({
        tone: 'danger',
        message: e instanceof Error ? e.message : 'Não foi possível concluir.',
      }),
  })

  const nos = previa.data?.nos ?? []
  // a raiz é o primeiro nó; o resto é o que vai junto
  const juntos = nos.slice(1)

  return (
    <Modal
      open
      onClose={onFechar}
      title="Concluir em cadeia"
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Fechar sem concluir
          </Button>
          <Button
            onClick={() => (onConcluir ? onConcluir() : concluir.mutate())}
            disabled={previa.isPending || concluir.isPending || concluindo || previa.isError}
          >
            {concluir.isPending || concluindo ? 'Concluindo…' : `Concluir ${nos.length} item(ns)`}
          </Button>
        </>
      }
    >
      {previa.isPending ? (
        <LoadingState rows={4} />
      ) : previa.isError ? (
        <Alert tone="danger" title="Não foi possível montar a lista">
          Tente de novo, ou conclua o item pela tela dele.
        </Alert>
      ) : (
        <div className="wi-cascata">
          <p className="wi-cascata__intro">
            Concluir <strong>{titulo}</strong>
            {juntos.length === 0
              ? ' — não há nada em aberto embaixo dele.'
              : ` conclui também ${juntos.length} ${juntos.length === 1 ? 'coisa' : 'coisas'} abaixo:`}
          </p>

          <ListaDaCascata nos={nos} linhasFeitas={false} />

          {/* o que o servidor recusar aparece no aviso depois; aqui fica o que
              a pessoa precisa saber ANTES de decidir */}
          <p className="wi-cascata__nota">
            Desmarcar depois pergunta se o que está embaixo é reaberto junto, ou só o item.
          </p>
        </div>
      )}
    </Modal>
  )
}

/** Um nó da cadeia como o servidor devolve (`NoDaCascata`, em `cascata-ordem.ts` da API). */
export type NoDaCascata = {
  tipo: 'item' | 'linha'
  id: string
  titulo: string
  codigo: string | null
  profundidade: number
}

/**
 * A árvore do que vai junto, com recuo por nível. Compartilhada pela conclusão
 * e pela reabertura em cadeia (SPEC-20261006-1355-desmarcar-em-cadeia), para
 * as duas janelas desenharem a mesma lista do mesmo jeito.
 *
 * `linhasFeitas` só troca o ícone das atividades: ao reabrir, elas estão
 * marcadas; ao concluir, em aberto.
 */
export function ListaDaCascata({
  nos,
  linhasFeitas,
}: {
  nos: readonly NoDaCascata[]
  linhasFeitas: boolean
}) {
  return (
    <ul className="wi-cascata__lista">
      {nos.map((no) => (
        <li
          key={`${no.tipo}-${no.id}`}
          className={`wi-cascata__linha${no.profundidade === 0 ? ' wi-cascata__linha--raiz' : ''}`}
          style={{ paddingLeft: no.profundidade * 18 }}
        >
          <i
            className={
              no.tipo === 'item' ? 'fas fa-cube' : linhasFeitas ? 'far fa-check-square' : 'far fa-square'
            }
            aria-hidden
          />
          {no.codigo ? <span className="wi-cascata__codigo">{no.codigo}</span> : null}
          <span className="wi-cascata__titulo">{no.titulo || 'Sem título'}</span>
        </li>
      ))}
    </ul>
  )
}
